<script setup>
import { ref } from 'vue';
import axios from 'axios';
import { VueFinalModal } from 'vue-final-modal';
import { IMaskComponent } from 'vue-imask';
import { useNotification } from '@kyvg/vue3-notification';
import VfmHeader from 'resources/js/components/VfmHeader.vue';
import ArrowDown from 'resources/inline-svg/arrow-down.svg';
import toggleLoading from 'resources/js/utils/toggle-loading';
import { KZ_PHONE_LENGTH } from 'resources/js/constants/phone';

const props = defineProps(['square', 'floors', 'modules']);
const emit = defineEmits(['close']);

const { notify } = useNotification()

const cities = ['Алматы', 'Астана'];

const name = ref();
const phone = ref();
const city = ref(cities[0]);

const nameError = ref();
const phoneError = ref();

function clearErrors() {
    nameError.value = '';
    phoneError.value = '';
}

function isFormValid() {
    let isValid = true;

    clearErrors();

    // Name validation
    if (!name.value.trim()) {
        nameError.value = 'Пожалуйста, введите ваше имя';
        isValid = false;
    }

    // Phone validation
    const phoneDigits = phone.value.replace(/\D/g, ''); // Убираем все нецифровые символы
    if (phoneDigits.length !== KZ_PHONE_LENGTH) {
        phoneError.value = 'Пожалуйста, введите корректный номер телефона';
        isValid = false;
    }

    return isValid;
}

const submit = () => {
    if (!isFormValid()) return;

    toggleLoading(true);

    const token = document.querySelector('meta[name="csrf-token"]').getAttribute('content');

    const formData = new FormData();

    formData.append('name', name.value);
    formData.append('phone', phone.value);
    formData.append('city', city.value);
    formData.append('square', props.square);
    formData.append('floors', props.floors);
    formData.append('modules', props.modules);

    axios
        .post('/prime/store', formData, {
            headers: {'X-CSRF-TOKEN': token},
        })
        .then(() => {
            notify({
                type: 'success',
                title: 'Спасибо! Заявка отправлена',
            });

            name.value = '';
            phone.value = '';

            emit('close');
        })
        .catch((e) => {
            const errors = e.response?.data?.errors;
            const error = e.response?.data?.error;

            let arr;

            if (typeof errors === 'object') {
                arr =  Object.values(errors);
            } else if (error) {
                arr = [error];
            } else {
                arr = ['Произошла ошибка при отправке запроса, попробуйте позже'];
            }

            Object.values(arr).forEach((item) => {
                notify({
                    type: 'error',
                    title: item,
                });
            })
        })
        .finally(() => {
            toggleLoading(false);
        });
};
</script>

<template>
    <VueFinalModal
        :esc-to-close="true"
        :click-to-close="true"
        class="prime-modal prime-form-modal"
    >
        <div class="prime-form-modal__cont">
           <VfmHeader class="prime-form-modal__header" @close="emit('close')"></VfmHeader>

            <form class="prime-form-modal__body" @submit.prevent="submit">
                <p class="prime-form-modal__title">Оставить заявку</p>

                <label class="prime-form-modal__label">Имя фамилия</label>
                <input
                    required
                    v-model="name"
                    :class="['ktui-input', { 'kr-ui-input--error': nameError }]"
                    placeholder="Введите ваши имя и фамилию"
                    @focus="clearErrors"
                >
                <div v-if="nameError" class="prime-form-modal__error">{{ nameError }}</div>

                <label class="prime-form-modal__label">Номер телефона</label>
                <IMaskComponent
                    required
                    type="tel"
                    mask="+{7} 000 000 00 00"
                    v-model="phone"
                    name="phone"
                    :class="['ktui-input', { 'ktui-input--error': phoneError }]"
                    placeholder="+7 *** *** ** **"
                    @focus="clearErrors"
                />
                <div v-if="phoneError" class="prime-form-modal__error">{{ phoneError }}</div>

                <label class="prime-form-modal__label">Город</label>
                <label class="select-wrapper">
                    <select v-model="city" class="select-wrapper__inp">
                        <option v-for="(item, index) in cities" :key="index">{{ item }}</option>
                    </select>

                    <ArrowDown class="select-wrapper__arrow" />
                </label>

                <div class="prime-form-modal__submit">
                    <button type="submit" class="prime-form-modal__submit-btn">Отправить</button>
                </div>
            </form>
        </div>
    </VueFinalModal>
</template>

<style lang="scss">
@import 'resources/sass/variables';

.prime-form-modal {
    .vfm__content {
        max-width: 534px;
    }

    &__cont {
        border-radius: 16px;
        background: linear-gradient(223.67deg, #3D3737 -0.51%, #312D2D 74.96%);

        @media screen and (max-width: $mobile-max-width) {
            border-radius: 16px 16px 0 0;
        }
    }

    &__body {
        padding: 0 56px 44px;

        @media screen and (max-width: $mobile-max-width) {
            @include padding-bottom-for-fixed(32px);

            padding: 0 16px 32px;
        }
    }

    &__header {
        border-bottom: none !important;
    }

    &__title {
        margin-bottom: 16px;
        font-weight: $font-weight-semibold;
        font-size: 20px;
        color: white;
        text-align: center;
    }

    &__label {
        display: block;
        margin: 12px 0 8px;
        font-size: 12px;
        font-weight: $font-weight-semibold;
        background: linear-gradient(90deg, #C37C28 0%, #E4A52A 40.91%, #E4D18B 95.79%);
        color: transparent;
        background-clip: text;
    }

    &__error {
        margin-top: 4px;
        color: white;
        font-size: 12px;
    }

    &__submit {
        margin-top: 36px;
        text-align: center;

        @media screen and (max-width: $mobile-max-width) {
            margin-top: 24px;
        }
    }

    &__submit-btn {
        @include reset-button;

        min-width: 240px;
        height: 46px;
        padding: 0 16px;
        color: white;
        font-size: 16px;
        border-radius: 23px;
        background: linear-gradient(90deg, #C88228 0%, #E4AF40 51%, #E4D18B 100%);


        @media (hover: hover) {
            &:hover {
                background: #E4AF40;
            }
        }
    }
}
</style>
